import React, { useState } from "react";

const SENIORITY_OPTIONS = ["intern", "junior", "mid", "senior", "lead", "principal", "staff"];

// Mirrors SENIORITY_YEARS in the sourcing backend (src/parser/intent.py). Seniority isn't a
// search filter on its own — CrustData's seniority tags are unreliable — so choosing one fills
// the experience range instead, the same way parsing a typed "senior" does.
const SENIORITY_YEARS = {
    intern: [0, 1],
    junior: [0, 2],
    mid: [3, 6],
    senior: [5, null],
    lead: [8, null],
    staff: [8, null],
    principal: [10, null],
};

// Matches CanonicalQuery.exclude_seen. Off by default: a search shows the true pool unless the
// recruiter asks for people to be left out.
const EXCLUDE_SEEN_OPTIONS = [
    { value: "", label: "Show everyone" },
    { value: "job", label: "Skip people already sourced for this job" },
    { value: "engaged", label: "Skip people already contacted, shortlisted or saved" },
];

const LABEL = "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5";
const SELECT = "w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 bg-white dark:bg-gray-900";
const NUMBER_INPUT = "w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100";

const numberOrNull = (value) => (value === "" ? null : Number(value));

const TagInput = ({ tags, onChange, placeholder }) => {
    const [input, setInput] = useState("");

    const addTag = (raw) => {
        const val = raw.trim().replace(/,$/, "");
        if (val && !tags.includes(val)) onChange([...tags, val]);
        setInput("");
    };

    return (
        <div className="flex flex-wrap gap-1.5 p-2.5 rounded-xl border border-gray-200 dark:border-gray-600 focus-within:border-purple-400 focus-within:ring-2 focus-within:ring-purple-100 min-h-[44px] bg-white dark:bg-white/5 cursor-text">
            {tags.map((tag, i) => (
                <span key={i} className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 text-sm">
                    {tag}
                    <button
                        type="button"
                        onClick={() => onChange(tags.filter((_, j) => j !== i))}
                        className="text-gray-400 dark:text-gray-500 hover:text-red-400 leading-none text-base"
                    >
                        ×
                    </button>
                </span>
            ))}
            <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(input); }
                    if (e.key === "Backspace" && !input && tags.length > 0) onChange(tags.slice(0, -1));
                }}
                onBlur={() => input.trim() && addTag(input)}
                placeholder={tags.length === 0 ? placeholder : ""}
                className="flex-1 min-w-[120px] outline-none text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 bg-transparent"
            />
        </div>
    );
};

const TagField = ({ label, field, placeholder, query, update }) => (
    <div>
        <label className={LABEL}>{label}</label>
        <TagInput
            tags={query[field] ?? []}
            onChange={v => update(field, v)}
            placeholder={placeholder}
        />
    </div>
);

const RangeField = ({ label, minField, maxField, query, update }) => (
    <div>
        <label className={LABEL}>{label}</label>
        <div className="flex items-center gap-2">
            <input
                type="number" min={0} placeholder="Min"
                value={query[minField] ?? ""}
                onChange={e => update(minField, numberOrNull(e.target.value))}
                className={NUMBER_INPUT}
            />
            <span className="text-gray-400 dark:text-gray-500 shrink-0">–</span>
            <input
                type="number" min={0} placeholder="Max"
                value={query[maxField] ?? ""}
                onChange={e => update(maxField, numberOrNull(e.target.value))}
                className={NUMBER_INPUT}
            />
        </div>
    </div>
);

/* The editable filter set, shared by the preview screen and the Edit Filters modal so the two
   can't drift apart. `onChange` takes a state updater, like a useState setter. */
export const FilterFields = ({ query, onChange }) => {
    const update = (field, value) => onChange(prev => ({ ...prev, [field]: value }));

    // Editing the skill list makes those skills the recruiter's own, so the search stops
    // loosening skills that came from the job on its own.
    const updateSkillList = (field, value) => onChange(prev => ({
        ...prev,
        [field]: value,
        filled_from_job: (prev.filled_from_job ?? []).filter(f => f !== "skills"),
    }));

    /* How many of the required skills a match needs. Changing this is tuning rather than
       authoring, so it leaves filled_from_job alone — but naming a level does stop the search
       choosing one itself. "Not required" is the last rung the search would have reached on its
       own: the skills only rank. For a job's phrases ("customer requirements analysis") that is
       often the difference between one match and a full page. */
    const setSkillMatch = (value) => onChange(prev => {
        if (value !== "none") {
            return { ...prev, skills_min_match: value === "" ? null : Number(value) };
        }
        const ranked = [...(prev.skills_required ?? []), ...(prev.skills_nice_to_have ?? [])];
        return {
            ...prev,
            skills_required: [],
            skills_min_match: null,
            skills_nice_to_have: [...new Set(ranked)],
            filled_from_job: (prev.filled_from_job ?? []).map(f => (f === "skills" ? "skills_nice_to_have" : f)),
        };
    });

    const setSeniority = (seniority) => onChange(prev => {
        const next = { ...prev, seniority };
        if (seniority && prev.experience_min == null && prev.experience_max == null) {
            const [min, max] = SENIORITY_YEARS[seniority];
            next.experience_min = min;
            next.experience_max = max;
        }
        return next;
    });

    const fieldProps = { query, update };
    const requiredCount = query.skills_required?.length ?? 0;

    return (
        <>
            <TagField label="Job title contains" field="roles" placeholder="e.g. product manager — press Enter to add" {...fieldProps} />
            {/* The role family CrustData groups titles into. It changes who counts as a match —
                a presales executive search also accepting presales engineers and consultants —
                so it stays the recruiter's call. */}
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                    type="checkbox"
                    checked={!!query.include_related_titles}
                    onChange={e => update("include_related_titles", e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-400"
                />
                <span>
                    Include related job titles
                    <span className="block text-xs text-gray-400 dark:text-gray-500">
                        e.g. a presales executive search also accepts presales engineers and consultants
                    </span>
                </span>
            </label>
            <TagField label="Exclude titles containing" field="exclude_titles" placeholder="e.g. manager — press Enter to add" {...fieldProps} />
            <TagField label="Locations" field="locations" placeholder="e.g. Pune, Mumbai — press Enter to add" {...fieldProps} />

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={LABEL}>Seniority</label>
                    <select
                        value={query.seniority ?? ""}
                        onChange={e => setSeniority(e.target.value || null)}
                        className={SELECT}
                    >
                        <option value="">Any seniority</option>
                        {SENIORITY_OPTIONS.map(s => (
                            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                    </select>
                </div>
                <RangeField label="Experience (years)" minField="experience_min" maxField="experience_max" {...fieldProps} />
            </div>

            <TagField label="Required Skills" field="skills_required" placeholder="e.g. Python — press Enter to add" {...fieldProps} update={updateSkillList} />
            {requiredCount > 1 && (
                <div>
                    <label className={LABEL}>Candidates need</label>
                    <select
                        value={query.skills_min_match ?? ""}
                        onChange={e => setSkillMatch(e.target.value)}
                        className={SELECT}
                    >
                        <option value="">All of the required skills</option>
                        {requiredCount > 2 && <option value={2}>At least 2 of them</option>}
                        <option value={1}>At least 1 of them</option>
                        <option value="none">Not required — rank by them instead</option>
                    </select>
                </div>
            )}
            <TagField label="Nice-to-have Skills" field="skills_nice_to_have" placeholder="e.g. AWS — press Enter to add" {...fieldProps} />
            <TagField label="Companies" field="companies" placeholder="e.g. Google, Stripe — press Enter to add" {...fieldProps} />
            <TagField label="Industries" field="industries" placeholder="e.g. Financial Services — press Enter to add" {...fieldProps} />
            <TagField label="Company description mentions" field="company_keywords" placeholder="e.g. fintech — press Enter to add" {...fieldProps} />
            <RangeField label="Company size (employees)" minField="company_headcount_min" maxField="company_headcount_max" {...fieldProps} />

            <div>
                <label className={LABEL}>People you've already seen</label>
                <select
                    value={query.exclude_seen ?? ""}
                    onChange={e => update("exclude_seen", e.target.value || null)}
                    className={SELECT}
                >
                    {EXCLUDE_SEEN_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>
        </>
    );
};

/* `busy`/`error`/`notice` are optional: callers that navigate away on submit (the results
   screen) leave them unset and keep the old fire-and-close behaviour. Callers that stay put —
   Find Candidates, where the modal sits over the list it's about to change — pass them so the
   search reports itself here instead of the modal vanishing into a silent reload. `notice`
   carries why a run found nobody, so the next edit starts from it. */
const FilterModal = ({ initialQuery, onClose, onRunSearch, busy = false, error = null, notice = null }) => {
    const [query, setQuery] = useState({ ...initialQuery });

    return (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#1B1524] rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">

                {/* header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-white">Edit Filters</h3>
                    <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none">×</button>
                </div>

                {/* scrollable fields */}
                <div className="overflow-y-auto flex-1 px-6 py-4 flex flex-col gap-4">
                    {notice && (
                        <div className="px-4 py-3 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                            {notice}
                        </div>
                    )}
                    <FilterFields query={query} onChange={setQuery} />
                </div>

                {/* footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-700/50 shrink-0">
                    {error && <p className="text-xs text-red-500 dark:text-red-400 mr-auto">{error}</p>}
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="px-5 py-2.5 rounded-full text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onRunSearch(query)}
                        disabled={busy}
                        className="px-6 py-2.5 rounded-full text-sm font-medium text-white disabled:opacity-60 transition-all"
                        style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                    >
                        {busy ? "Running…" : "Run Search"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FilterModal;
